const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/**
 * Legacy SVG bar + forecast-area + target-line + NOW-separator chart.
 * Preserved style requested by the user — shows Jan–N as bars with
 * value labels, then N+1–Dec as dashed forecast line + light fill,
 * target line as dashed horizontal, NOW divider between past/future.
 *
 * Props:
 *   actual        Array<number|null> length 12 — past months have values,
 *                 future months are null.
 *   forecast      Array<number|null> length 12 — future months have values,
 *                 past months typically null.
 *   target        Scalar target line value.
 *   unit          '%' | '€K' | '' ('kr' suffix) — drives label formatting.
 *   label         String used as gradient id (must be unique per chart).
 *   height        SVG viewBox height (default 180).
 *   actualColor   Bar + actual-line colour.
 *   forecastColor Dashed-line + area-fill colour.
 *   targetColor   Target line colour.
 *   areaOpacity   Forecast area fill opacity baseline (default 0.13).
 */
export default function ComboChart({
  actual, forecast, target,
  unit = "", label = "", height = 180,
  actualColor = "#3b7ffa", forecastColor = "#60a5fa",
  targetColor = "#f59e0b", areaOpacity = 0.13,
}) {
  const W = 780, H = height;
  const PAD = { top: 20, right: 52, bottom: 36, left: 52 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  const allVals = [...actual.filter(v => v !== null), ...forecast.filter(v => v !== null), target];
  const dataMin = allVals.length > 0 ? Math.min(...allVals) : 0;
  const dataMax = allVals.length > 0 ? Math.max(...allVals) : 100;
  const yMin = Math.max(0, Math.floor((dataMin * 0.88) / 10) * 10);
  let yMax = Math.ceil((dataMax * 1.08) / 10) * 10;
  if (unit === "%" && yMax < 120) yMax = 120;
  const yRange = yMax - yMin || 1;

  const scaleY = v => PAD.top + innerH - ((v - yMin) / yRange) * innerH;
  const slotW = innerW / MONTHS.length;
  const scaleX = i => PAD.left + (i + 0.5) * slotW;
  const barW = slotW * 0.62;

  const yTicks = [];
  const tickStep = Math.ceil(yRange / 5 / 10) * 10 || 10;
  for (let t = yMin; t <= yMax + 0.01; t += tickStep) yTicks.push(t);

  const fPoints = forecast
    .map((v, i) => v !== null ? { x: scaleX(i), y: scaleY(v) } : null)
    .filter(Boolean);
  let fAreaPath = "", fLinePath = "";
  if (fPoints.length > 0) {
    fLinePath = fPoints.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
    const bottom = scaleY(yMin);
    fAreaPath = fLinePath + ` L${fPoints[fPoints.length - 1].x},${bottom} L${fPoints[0].x},${bottom} Z`;
  }

  const aPoints = actual.map((v, i) => v !== null ? { x: scaleX(i), y: scaleY(v) } : null).filter(Boolean);
  const aLinePath = aPoints.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

  const targetY = scaleY(target);
  const tLine = `M${PAD.left},${targetY.toFixed(1)} L${PAD.left + innerW},${targetY.toFixed(1)}`;
  const lastActualIdx = actual.reduce((m, v, i) => v !== null ? i : m, -1);

  const formatVal = (v) => unit === "€K" ? `${v}K kr` : unit === "%" ? `${v}%` : `${v} kr`;

  return (
    <div className="w-full overflow-hidden">
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", display: "block" }} xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id={`fg-${label}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={forecastColor} stopOpacity={areaOpacity * 2} />
            <stop offset="100%" stopColor={forecastColor} stopOpacity="0" />
          </linearGradient>
        </defs>

        {yTicks.map(t => (
          <line key={t} x1={PAD.left} y1={scaleY(t).toFixed(1)} x2={PAD.left + innerW} y2={scaleY(t).toFixed(1)}
            stroke="#14213d" strokeWidth="1" />
        ))}

        {fAreaPath && <path d={fAreaPath} fill={`url(#fg-${label})`} />}

        {actual.map((v, i) => v !== null ? (
          <rect key={i}
            x={(scaleX(i) - barW / 2).toFixed(1)} y={scaleY(v).toFixed(1)}
            width={barW.toFixed(1)} height={(scaleY(yMin) - scaleY(v)).toFixed(1)}
            rx="3" fill={actualColor} opacity="0.9" />
        ) : null)}

        {forecast.map((v, i) => v !== null ? (
          <rect key={i}
            x={(scaleX(i) - barW / 2).toFixed(1)} y={scaleY(v).toFixed(1)}
            width={barW.toFixed(1)} height={(scaleY(yMin) - scaleY(v)).toFixed(1)}
            rx="3" fill={forecastColor} opacity="0.35" />
        ) : null)}

        {fLinePath && <path d={fLinePath} fill="none" stroke={forecastColor} strokeWidth="1.5" strokeDasharray="5,3" opacity="0.8" />}
        {aLinePath && <path d={aLinePath} fill="none" stroke={actualColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />}

        <path d={tLine} fill="none" stroke={targetColor} strokeWidth="1.5" strokeDasharray="6,4" opacity="0.7" />
        <text x={PAD.left + innerW + 4} y={targetY + 4} fill={targetColor} fontSize="10" fontFamily="'Noto Sans Mono',monospace" opacity="0.85">target</text>

        {aPoints.map((p, i) => (
          <circle key={i} cx={p.x.toFixed(1)} cy={p.y.toFixed(1)} r="4" fill={actualColor} stroke="#0a1528" strokeWidth="1.5" />
        ))}
        {fPoints.map((p, i) => (
          <circle key={i} cx={p.x.toFixed(1)} cy={p.y.toFixed(1)} r="3" fill={forecastColor} stroke="#0a1528" strokeWidth="1.5" opacity="0.8" />
        ))}

        {actual.map((v, i) => v !== null ? (
          <text key={i} x={scaleX(i).toFixed(1)} y={(scaleY(v) - 6).toFixed(1)}
            textAnchor="middle" fill={actualColor} fontSize="10" fontFamily="'Noto Sans Mono',monospace" fontWeight="600">
            {formatVal(v)}
          </text>
        ) : null)}

        {lastActualIdx >= 0 && lastActualIdx < MONTHS.length - 1 && (
          <>
            <line
              x1={((scaleX(lastActualIdx) + scaleX(lastActualIdx + 1)) / 2).toFixed(1)} y1={PAD.top}
              x2={((scaleX(lastActualIdx) + scaleX(lastActualIdx + 1)) / 2).toFixed(1)} y2={PAD.top + innerH}
              stroke="#1c2e52" strokeWidth="1" strokeDasharray="3,3" />
            <text
              x={((scaleX(lastActualIdx) + scaleX(lastActualIdx + 1)) / 2 + 4).toFixed(1)}
              y={(PAD.top + 10).toFixed(1)}
              fill="#364f72" fontSize="9" fontFamily="'Noto Sans',sans-serif" letterSpacing="0.1em">
              NOW
            </text>
          </>
        )}

        {yTicks.map(t => (
          <text key={t} x={PAD.left - 6} y={(scaleY(t) + 4).toFixed(1)}
            textAnchor="end" fill="#364f72" fontSize="10" fontFamily="'Noto Sans Mono',monospace">
            {formatVal(t).replace(/^(\d+)/, m => m)}
          </text>
        ))}

        {MONTHS.map((m, i) => (
          <text key={i} x={scaleX(i).toFixed(1)} y={PAD.top + innerH + 18}
            textAnchor="middle" fill={actual[i] !== null ? "#8fa4c8" : "#364f72"} fontSize="11" fontFamily="'Noto Sans',sans-serif" fontWeight={actual[i] !== null ? "600" : "400"}>
            {m}
          </text>
        ))}

        <line x1={PAD.left} y1={PAD.top + innerH} x2={PAD.left + innerW} y2={PAD.top + innerH} stroke="#14213d" strokeWidth="1" />
      </svg>
    </div>
  );
}

/**
 * Adapter: turn the adapter-produced `rows` shape
 * (e.g. `[{ month: 'Jan', Actual: 87, Forecast: 80, Target: 85 }, ...]`)
 * into the two parallel arrays ComboChart expects. Months that have no
 * value for a given series yield `null` in the output so ComboChart can
 * render gaps correctly.
 *
 *   rows      — 12-length array keyed by month label
 *   actualKey — field name for past actuals (default "Actual")
 *   forecastKey — field name for future forecasts (default "Forecast")
 *   coerceNumber — optional: if the row value is 0 it's treated as null
 *                  (useful when 0 means "no data" rather than literal zero).
 *
 * The split between past/future is driven by which months have a non-null
 * actual value; any month past the last non-null actual falls back to
 * `forecastKey`. This mirrors the legacy behaviour.
 */
export function toComboArrays(rows, actualKey = "Actual", forecastKey = "Forecast") {
  const actual = Array(12).fill(null);
  const forecast = Array(12).fill(null);
  if (!Array.isArray(rows)) return { actual, forecast };
  let lastActualIdx = -1;
  for (let i = 0; i < Math.min(12, rows.length); i++) {
    const a = rows[i]?.[actualKey];
    if (a != null && a !== 0) {
      actual[i] = Number(a);
      lastActualIdx = i;
    }
  }
  // Forecast spans only the months after the last actual; also seed the
  // last-actual month itself so the line visually joins bars → line.
  for (let i = Math.max(0, lastActualIdx); i < 12; i++) {
    const f = rows[i]?.[forecastKey];
    if (f != null) forecast[i] = Number(f);
  }
  // If the last actual month has a value, mirror it onto the forecast
  // start so the dashed line begins at the top of the last bar.
  if (lastActualIdx >= 0 && forecast[lastActualIdx] == null && actual[lastActualIdx] != null) {
    forecast[lastActualIdx] = actual[lastActualIdx];
  }
  return { actual, forecast };
}
